import {BaseModel} from '@/core/standard/BaseModel';
import CommandService from '@/modules/requests/commands/CommandService';
import {CommandProtocol, ICommandModel, ICommands} from '@/modules/requests/commands/ICommandModel';
import PaymentsService from '@/modules/requests/requestsTables/PaymentsService';
import {GenericMethod, ResProtocol} from '@/core/Types/IRepository';
import {RoleProtocol} from '@/modules/auth/IRoleModel';
import GlobalConfigs from '@/setup/GlobalConfigs/GlobalConfigs';
import {IModifierModel} from '@/modules/modifiers/IModifierModel';

const command = {
  price: 0,
  status: '',
  type: '',
  table: '',
  serial: '',
  new_table: '',
  user: '',
  all_discounted: false,
  client: '',
  command: '',
  discount: {value: 0, type: ''},
  invalidated_by: '',
  from_client: '',
  products: [],
  command_guests: 1,
  details: '',
};

class CommandModel extends BaseModel<ICommandModel, CommandProtocol, typeof CommandService> {
  public modelName = 'Comanda';
  public request = 'JSON';

  constructor() {
    super(command, CommandService);
  }

  public async mapRow(row: ICommandModel, id: string | number) {
    if (id) {
      const data: ICommands = await this.get({
        id,
      }) as any;
      if (data.products.length) {
        this.mapCommandData(data, row);
      }
      super.mapRow(data);
      return data;
    }
  }

  public mapCommandData(data: ICommands, row: ICommandModel) {
    data.products.forEach((product) => {
      product.uniqueKey = `${product.product_name}${Math.floor((Math.random() * 100000) + 1)}`;
      product.name = product.product_name;
      product.price = +Number(+product.importe / +product.qty).toFixed(2);
    });
    data.products = Object.assign([], data.products);
    data.invalidated_by = row.invalidated_by;
    data.type = 'local';
  }

  public async mapToReports(row, id: string | number) {
    if (id) {
      const data: ICommands = await this.getToReports({
        id,
      }) as any;
      if (data.products.length) {
        this.mapCommandData(data, row);
      }
      super.mapRow(data);
      return data;
    }
  }

  public async getToReports(query: GenericMethod, toast: any = null, toastConfig: any = {}) {
    const response = await this.service.retrieveReport(query as GenericMethod);
    if (response.complete) {
      return this.handleSuccess(response.data, response.message, toast, toastConfig);
    } else {
      this.handleError(response, toast, toastConfig);
      return false;
    }

  }

  public mapProduct(product) {
    return {
      id: product.id,
      qty: product.qty,
      name: product.name,
      items: product.items,
      modifiers: product.modifiers,
      price: product.price,
      order: product.order,
      details: product.details,
      is_discounted: product.is_discounted,
      request_table: product.request_table,
    };
  }

  public mapItem(item) {
    return {
      id: item.id,
      name: item.name,
      qty: item.qty,
      price: item.price,
      type: item.type,
      unit_name: item.unit_name,
      unit_value: item.unit_value,
    };
  }

  public mapProducts(products) {
    let productsToSend: any[] = [];
    products.forEach(element => {
      const product = this.mapProduct(element);
      if (GlobalConfigs.use_modifiers && product.modifiers?.length) {
        product.modifiers = this.mapModifiers(product.modifiers);
      }

      if (!GlobalConfigs.use_modifiers && product.items?.length) {
        product.items = product.items.map((item) => this.mapItem(item));
      }
      productsToSend.push(product);
    });
    return productsToSend;
  }

  public mapModifiers(modifiers: IModifierModel[]) {
    return modifiers.filter((modifier) => {
      return (modifier.options = modifier.options.filter((option) => {
        if (option.modifiers && option.modifiers.length) {
          option.modifiers = this.mapModifiers(option.modifiers);
        }
        return option.selected;
      })).length;
    });
  }

  public async create(data, toast: any = null, toastConfig: any = {}) {
    const parsedData = JSON.parse(data);
    parsedData.products = this.mapProducts(parsedData.products);

    const query = {
      query: parsedData,
      method: 'POST',
    };
    const response = await this.service.insert(query as GenericMethod);
    if (response.complete) {
      return this.handleSuccess(response.data, response.message, toast, toastConfig);
    } else {
      this.handleError(response, toast, toastConfig);
      return false;
    }
  }

  public async update(data, toast: any = null, toastConfig: any = {}) {
    const parsedData = JSON.parse(data);
    parsedData.products = this.mapProducts(parsedData.products);
    const query = {
      query: parsedData,
      url: `${parsedData.id}`,
      method: 'PUT',
    };

    const response = await this.service.update(query as GenericMethod);
    if (response.complete) {
      return this.handleSuccess(response.data, response.message, null);
    } else {
      this.handleError(response, toast, toastConfig);
      return false;
    }
  }

  public async getActiveTables(query) {
    const request = await this.service.getActiveTables({method: 'GET', version: 'v2', ...query});
    if (request && request.data) {
      return {data: request.data};
    }
  }

  public async closeCommandWithoutPayment(data: Record<string, any>, toast) {
    const response = await this.service.closeCommandWithoutPayment({method: 'POST', query: data});
    if (data.complete) {
      this.handleSuccess(response, data.message, toast);
      return true;
    }
    this.handleError(response, toast);
    return false;
  }

  public async closeCommand(commandId: string | number, type: string, tips: number | string = 0, toast) {
    const value: { [keyName: string]: any } = {} as { [keyName: string]: any };
    value.id = commandId;
    if (Number(tips) !== 0) {
      value.tip_amount = Number(tips);
      value.tip_option = 'other';
    }
    if (type === 'withCard') {
      value.credit_card = true;
    }
    try {
      const {complete} = await this.service.closeCommand({method: 'POST', query: JSON.stringify(value)});
      return complete;
    } catch (e) {
      this.handleError(e as any, toast);
      return {data: [], total: 0};
    }
  }

  public async changeStatusByCommand(commandId: string | number) {
    const {complete} = await this.service.changeStatusByCommand({
      method: 'POST',
      query: JSON.stringify({id: commandId}),
    });
    return complete;
  }

  public async checkCommand(commandId: string | number) {
    const {data} = await this.service.checkCommand({method: 'POST', query: JSON.stringify({id: commandId})});
    return !!data;

  }

  public async invalidate(commandId: string) {
    return await this.service.invalidate({method: 'POST', query: JSON.stringify({id: commandId})});
  }

  public async printAll(sendData, toast) {
    const data = await this.service.printAll({method: 'POST', query: sendData});
    if (data.complete) {
      this.handleSuccess(data, data.message, toast);
      return data;
    }
    this.handleError(data, toast);
    return {data: [], total: 0};
  }

  public async printAllFromReport(sendData, toast) {
    const data = await this.service.printAllFromReport({method: 'POST', query: sendData});
    if (data.complete) {
      this.handleSuccess(data, data.message, toast);
      return data;
    }
    this.handleError(data, toast);
    return {data: [], total: 0};
  }

  public async printSplit(sendData, toast) {
    try {
      const {complete} = await this.service.printSplit({method: 'POST', query: sendData});
      if (complete) {
        return complete;
      }
    } catch (e) {
      this.handleError(e as any, toast);
      return {data: [], total: 0};
    }
  }

  public async printDeliverySplit(sendData, toast) {
    try {
      const {complete} = await this.service.printDeliverySplit({method: 'POST', query: sendData});
      if (complete) {
        return complete;
      }
    } catch (e) {
      this.handleError(e as any, toast);
      return {data: [], total: 0};
    }
  }

  public async restorePayment(sendData) {
    const {complete} = await PaymentsService.restorePayments({method: 'POST', url: 'restore', query: sendData});
    if (complete) {
      return complete;
    }
  }

  public async getCommand(sendData) {
    const {data, complete} = await this.service.getCommand({method: 'POST', query: sendData});
    if (complete) {
      return data;
    }
  }

  public async sendPayment(sendData) {
    const {complete} = await PaymentsService.makeSplitPaymentByProducts({
      method: 'POST',
      url: 'by_products',
      query: sendData,
    });
    if (complete) {
      return complete;
    }
  }

  public async sendDeliveryPayment(sendData) {
    const {complete} = await PaymentsService.makeSplitPaymentByProducts({
      method: 'POST',
      url: 'by_products/delivery',
      query: sendData,
    });
    if (complete) {
      return complete;
    }
  }

  public async getPayments(sendData) {
    const {data, complete} = await PaymentsService.getPaymentsType({
      method: 'POST',
      query: sendData,
      url: 'get_by_command',
    });
    if (complete) {
      return data;
    }
  }

  public async checkPrices(sendData) {
    const {data, complete} = await PaymentsService.check({method: 'POST', query: sendData, url: 'check'});
    if (complete) {
      return data;
    }
  }

  public async restoreItem(sendData) {
    const {data, complete} = await PaymentsService.restoreItem({method: 'POST', query: sendData, url: 'restore_item'});
    if (complete) {
      return data;
    }
  }

  public async restoreItemDelivery(sendData) {
    const {data, complete} = await PaymentsService.restoreItem({method: 'POST', query: sendData, url: 'restore_item/delivery'});
    if (complete) {
      return data;
    }
  }

  public async makeSplitPay(commandId: string | number, type: string, price: string,
                            totalPrice: number) {
    const value: { [keyName: string]: any } = {} as { [keyName: string]: any };
    value.command = commandId;
    value.price = price;
    value.total_price = totalPrice;
    if (type === 'withCard') {
      value.credit_card = true;
    }
    const {data} = await PaymentsService.makeSplitPaymentByPrice({
      method: 'POST', url: 'by_price',
      query: JSON.stringify(value),
    });
    return data;
  }

  public async list(query: GenericMethod<RoleProtocol>, toast: any = null, toastConfig: any = null) {
    try {
      const {
        data: {results: data, total},
        complete,
        message,
      } = await this.service.getAll({...query} as any) as ResProtocol;
      if (complete) {
        return this.handleSuccess({data, total}, message, toast, toastConfig);
      }
    } catch (e) {
      this.handleError(e as any, toast, toastConfig);
      return {data: [], total: 0};
    }
  }

  public async sendTicketByEmailLocalAll(data: any, toast) {
    const response = await this.service.sendTicketByEmailLocalAll({method: 'POST', query: data});
    if (response.complete) {
      this.handleSuccess(response, response.message, toast);
      return response;
    }
    this.handleError(response, toast);
    return {response: [], total: 0};
  }

  public async changeTable(data: any, toast) {
    const response = await this.service.changeTable({method: 'POST', query: data});
    if (response.complete) {
      this.handleSuccess(response, response.message, toast);
      return response;
    }
    this.handleError(response, toast);
    return {response: [], total: 0};
  }

  public async changePaymentType(commandId: string | number, paymentType: string, toast) {
    const response = await this.service.changePaymentType({
      method: 'POST',
      id: commandId,
      query: {
        payed_with: paymentType
      },
    });
    if (response.complete) {
      this.handleSuccess(response, response.message, toast);
      return response.complete;
    }
    this.handleError(response, toast);
    return false;
  }
}

export default CommandModel as new() => ICommands;
